import { useState } from 'react';
import { Link } from 'react-router-dom';
import SectionHeading from '@/components/base/SectionHeading';
import { featuredComments } from '@/mocks/comments';
import { formatHeat } from '@/utils/format';

type Comment = (typeof featuredComments)[number];

function CommentCard({ comment }: { comment: Comment }) {
  const [liked, setLiked] = useState(false);
  const likes = comment.likes + (liked ? 1 : 0);

  return (
    <article className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-accent-300 md:p-5">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-base text-secondary-900">
          {comment.user.slice(0, 1)}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm text-foreground-950">{comment.user}</p>
          <p className="text-xs text-foreground-500">来自《{comment.scene}》</p>
        </div>
        <span className="ml-auto shrink-0 rounded-full bg-accent-100 px-2.5 py-1 text-xs text-accent-900">
          {comment.tag}
        </span>
      </div>

      <p className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">{comment.text}</p>

      <div className="mt-5 flex items-center justify-between border-t border-background-200 pt-3">
        <button
          type="button"
          onClick={() => setLiked((prev) => !prev)}
          aria-pressed={liked}
          aria-label={liked ? '取消点赞' : '点赞这条热评'}
          className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors ${
            liked ? 'bg-primary-100 text-primary-700' : 'text-foreground-500 hover:bg-background-100'
          }`}
        >
          <i className={liked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} aria-hidden="true" />
          {formatHeat(likes)}
        </button>
        <Link
          to={`/scene/${comment.sceneId}`}
          className="flex items-center gap-1 text-xs text-foreground-600 transition-colors hover:text-primary-600"
        >
          看原现场
          <i className="ri-arrow-right-s-line" aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}

export default function HotCommentsSection() {
  return (
    <section id="comments" className="scroll-mt-24 bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-content px-4 md:px-6">
        <SectionHeading
          eyebrow="热评精选"
          title="评论区才是真正的主场"
          description="一句话能把整件事讲明白的人，值得拥有单独的展位。点个赞，让好评论被更多人看到。"
        />

        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {featuredComments.map((comment) => (
            <CommentCard key={comment.id} comment={comment} />
          ))}
        </div>
      </div>
    </section>
  );
}